import { DETAIL_LOTTERIES_RULES } from '~/utils/constants/lotteriesRules.js'
import {
	ID_EL_GORDO,
	ID_EURO_MILLIONS,
	ID_POWERBALL,
	ID_MEGASENA,
	ID_NATIONAL,
	ID_UK_LOTTO,
	ID_THUNDERBALL,
	ID_MEGA_MILLIONS,
	ID_LOTTO_AMERICA,
	ID_NATIONAL_THURSDAY,
	ID_QUINA,
	ID_LOTTO_FACIL,
	ID_EL_GORDO_NAVIDAD,
	ID_EURODREAMS_PT,
	ID_EURODREAMS_FR,
	ID_LOTO_FR,
	ID_KENO_FR,
} from '~/utils/constants/idsLotteries.js'

/**
 * Returns the full list of lotteries enriched with localised `description` and `rules` strings.
 * Content is recalculated reactively when the active locale changes.
 * Also exposes `details` as a computed dictionary keyed by lottery ID for direct lookup.
 */
export const useLotteriesRules = () => {
	const { t, locale } = useI18n()
	const eurodreamsContent = {
		description: t(
			'EuroDreams is a European annuity lottery that offers prizes as monthly payments over many years. The top prize provides winners with a tax-free income for three decades',
		),
		rules: [
			t('Choose 6 main numbers from 1 to 40'),
			t('Select 1 "Dream Number" from 1 to 5'),
			t('Matching all 7 numbers wins the top prize of €20,000 per month for 30 years'),
		],
	}

	// Generador para evitar repetir código
	const buildDetails = () => ({
		[ID_EURO_MILLIONS]: {
			description: t(
				'EuroMillions is a popular European lottery played across several countries. Players choose numbers hoping to match the combination drawn every week. The game is known for its big jackpots and simple structure',
			),
			rules: [
				t('Choose 5 main numbers from 1 to 50'),
				t('Choose 2 Lucky Star numbers from 1 to 12'),
				t('To win the jackpot, all 5 main numbers and both Lucky Stars must match the draw'),
			],
		},
		[ID_EL_GORDO]: {
			description: t(
				'A weekly Spanish lottery where players choose five main numbers plus a Key Number, aiming to match the full combination',
			),
			rules: [
				t('Choose 5 main numbers from 1 to 54'),
				t('Choose 1 Key Number (Número Clave) from 0 to 9'),
				t('Match all 5 main numbers and the Key Number to win the jackpot'),
			],
		},
		[ID_POWERBALL]: {
			description: t(
				'Powerball is a major U.S. lottery known for some of the world’s biggest jackpots. You select numbers and a special Powerball to try to match the draw',
			),
			rules: [
				t('Pick 5 main numbers from 1 to 69'),
				t('Pick 1 Powerball number from 1 to 26'),
				t('Match all 5 main numbers and the Powerball to win the jackpot'),
			],
		},
		[ID_MEGASENA]: {
			description: t(
				'Mega-Sena is Brazil’s biggest lottery. You pick 6 numbers and hope to match them all in the national draw',
			),
			rules: [t('Choose 6 numbers from 1 to 60'), t('Match all 6 numbers drawn to win the main prize')],
		},
		[ID_NATIONAL]: {
			description: t(
				"Loteria Nacional is a Spanish lottery based on pre-printed ticket numbers. It's drawn regularly and prizes are based on exact number matches",
			),
			rules: [
				t('Buy a ticket with a full printed number'),
				t('Match your full number exactly with the winning number to win the top prize'),
			],
		},
		[ID_UK_LOTTO]: {
			description: t(
				'UK Lotto is the national lottery of the United Kingdom. You choose 6 numbers and win if all are drawn',
			),
			rules: [t('Pick 6 numbers from 1 to 59'), t('Match all 6 numbers drawn to win the jackpot')],
		},
		[ID_THUNDERBALL]: {
			description: t(
				'Thunderball is a UK lottery game that offers more chances to win with an extra ball. It’s drawn multiple times a week',
			),
			rules: [
				t('Choose 5 numbers from 1 to 39'),
				t('Choose 1 Thunderball number from 1 to 14'),
				t('Match all 5 main numbers and the Thunderball to win the top prize'),
			],
		},
		[ID_MEGA_MILLIONS]: {
			description: t(
				'Mega Millions is a large U.S. lottery played in many states. It’s known for its big jackpots and simple play method',
			),
			rules: [
				t('Pick 5 main numbers from 1 to 70'),
				t('Pick 1 Mega Ball number from 1 to 25'),
				t('Match all 5 main numbers and the Mega Ball to win the jackpot'),
			],
		},
		[ID_LOTTO_AMERICA]: {
			description: t(
				'Lotto America is a U.S. lottery game available in several states. Players choose numbers and a Star Ball to try for the top prize',
			),
			rules: [
				t('Choose 5 numbers from 1 to 52'),
				t('Choose 1 Star Ball number from 1 to 10'),
				t('Match all 5 main numbers and the Star Ball to win the top prize'),
			],
		},
		[ID_NATIONAL_THURSDAY]: {
			description: t(
				'This is the Thursday version of Spain’s Lotería Nacional. It uses pre-printed ticket numbers and works just like the main draw',
			),
			rules: [
				t('Buy a ticket with a printed 5-digit number'),
				t('Match all 5 digits exactly with the winning number to win the top prize'),
			],
		},
		[ID_QUINA]: {
			description: t(
				'Quina is a popular daily lottery in Brazil. It’s easy to play — just choose 5 numbers from a large pool',
			),
			rules: [t('Choose 5 numbers from 1 to 80'), t('Match all 5 numbers drawn to win the main prize')],
		},
		[ID_LOTTO_FACIL]: {
			description: t(
				'Lotofácil is a Brazilian lottery known for being simple and frequent. You pick 15 numbers from a small pool, making it easier to play',
			),
			rules: [t('Choose 15 numbers from 1 to 25'), t('Match all 15 numbers drawn to win the top prize')],
		},
		[ID_EL_GORDO_NAVIDAD]: {
			description: t(
				'Spain’s famous Christmas lottery, where tickets are pre-printed with a 5-digit number and often shared in parts',
			),
			rules: [
				t('Buy a ticket with a 5-digit number'),
				t('If all 5 digits match the winning number exactly, you win the top prize'),
			],
		},
		[ID_EURODREAMS_PT]: {
			...eurodreamsContent,
		},
		[ID_EURODREAMS_FR]: {
			...eurodreamsContent,
		},
		[ID_LOTO_FR]: {
			description: t(
				'Loto is the national lottery of France, operated by Française des Jeux (FDJ). It holds three draws a week and features a jackpot that rolls over and increases when not won',
			),
			rules: [
				t('Pick 5 main numbers from a pool of 1 to 49'),
				t('Pick 1 "Lucky Number" from a pool of 1 to 10'),
				t('Match all 5 main numbers and the Lucky Number to win the jackpot'),
			],
		},
		[ID_KENO_FR]: {
			description: t(
				'Keno, known in France as "Keno Gagnant à Vie" (Keno Winner for Life), is a fast-paced lottery with twice-daily draws. It offers flexible play, allowing players to choose the number of numbers they wish to bet on',
			),
			rules: [
				t('Choose between 2 and 10 numbers from a grid of 1 to 70'),
				t('Select your stake (€1, €2, €3, €5, or €10)'),
				t(
					'20 numbers are drawn, and prizes are won based on how many of your chosen numbers match the winning numbers',
				),
			],
		},
	})

	// Se recalcula automáticamente cuando cambia locale
	const detailsComputed = computed(() => {
		const _ = locale.value
		return buildDetails()
	})

	const lotteries = computed(() =>
		DETAIL_LOTTERIES_RULES.map((lottery) => ({
			...lottery,
			...(detailsComputed.value[lottery.id] || {}),
		})),
	)

	return {
		lotteries: lotteries.value, // array reactivo con description/rules traducidas
		details: detailsComputed, // por si necesitas el diccionario
	}
}
